import { useEffect, useState } from 'react';
import axios from 'axios';
import { END_POINT } from '../Redux/AdminReducer/action';
import { RefrenceList, PatientRefrenceList, educationLevels, religionOptions } from '../Files/dropdownOptions';

// Used only when the clinic's lists cannot be loaded or a list is empty.
export const PUBLIC_DROPDOWN_FALLBACKS = {
  gender: ['Male', 'Female', 'Other'],
  marital_status: ['Single', 'Married', 'Divorced'],
  blood_group: ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'],
  religion: religionOptions,
  education: educationLevels,
  profession: ['Homemaker', 'Teacher', 'Student', 'Retired', 'IT', 'Business', 'Engineering', 'Medical', 'Other'],
  industry: [],
  mother_tongue: ['Hindi', 'English', 'Marathi', 'Gujarati', 'Bengali', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Punjabi', 'Odia', 'Urdu', 'Other'],
  reference_for_enquiry: RefrenceList,
  patient_reference: PatientRefrenceList,
};

const norm = (value) => String(value ?? '').trim().toLowerCase();

// Case-insensitive, trimmed comparison (same rule the rest of the app uses).
export const sameOption = (a, b) => norm(a) !== '' && norm(a) === norm(b);

// Keeps a saved value selectable: if it matches an option ignoring case, that
// option is shown with the saved spelling (no near-duplicate entry); otherwise
// the saved value is appended.
export const withCurrentValue = (options, current) => {
  const list = Array.isArray(options) ? options : [];
  if (current === undefined || current === null || String(current).trim() === '') return list;
  const index = list.findIndex((item) => sameOption(item, current));
  if (index === -1) return [...list, current];
  const next = [...list];
  next[index] = current;
  return next;
};

/**
 * Option lists for the public patient forms, read from the clinic's DropDowns
 * configuration via the unauthenticated /public/dropdown-options endpoint.
 * Every key starts on (and falls back to) the built-in list; `overrides` lets a
 * form keep its own previous hardcoded list as the fallback for a key.
 */
export default function usePublicDropdownOptions(overrides) {
  const [fallbacks] = useState(() => ({ ...PUBLIC_DROPDOWN_FALLBACKS, ...(overrides || {}) }));
  const [options, setOptions] = useState(fallbacks);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${END_POINT}/public/dropdown-options`, {
        headers: { 'X-Frontend-Host': window.location.host },
      })
      .then((res) => {
        const data = res?.data?.data || {};
        if (cancelled) return;
        setOptions(
          Object.fromEntries(
            Object.entries(fallbacks).map(([key, fallback]) => [
              key,
              Array.isArray(data[key]) && data[key].length ? data[key] : fallback,
            ])
          )
        );
      })
      .catch(() => {
        // Keep the built-in lists.
      });
    return () => {
      cancelled = true;
    };
  }, [fallbacks]);

  return options;
}
